import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd, { getOrganizationSchema, getWebPageSchema, getBreadcrumbSchema } from '@/components/feature/JsonLd';
import useScrollToTop from '@/hooks/useScrollToTop';
import { siteInfo, aboutContent } from '@/mocks/siteInfo';

export default function AboutPage() {
  useScrollToTop();
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const pageTitle = `关于我们 | ${siteInfo.name} - 4K超清影视免费在线观看平台`;
  const pageDesc = `${siteInfo.name}成立于${siteInfo.founded}年，是国内领先的4K超清影视在线播放平台。${siteInfo.description}`;

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: aboutContent.faqs.map(f => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.answer,
      },
    })),
  };

  return (
    <>
      <JsonLd {...getOrganizationSchema()} />
      <JsonLd {...getWebPageSchema(pageTitle, pageDesc, `${siteInfo.domain}/about`)} />
      <JsonLd {...getBreadcrumbSchema([
        { name: '首页', url: siteInfo.domain },
        { name: '关于我们', url: `${siteInfo.domain}/about` },
      ])} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <section className="bg-background-100 border-b border-background-200">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-10 md:py-14 text-center">
              <div className="flex items-center gap-2 text-sm text-foreground-500 mb-4 justify-center">
                <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-900">关于我们</span>
              </div>
              <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-950 mb-4">
                关于{siteInfo.name}
              </h1>
              <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
                {siteInfo.description}
              </p>
            </div>
          </section>

          <section className="py-10 md:py-14">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
                {aboutContent.intro.map((item) => (
                  <div key={item.title}>
                    <h2 className="text-xl font-bold font-heading text-foreground-950 mb-4">{item.title}</h2>
                    <p className="text-sm text-foreground-700 leading-relaxed">{item.content}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 text-center mb-8">
                我们的优势
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {aboutContent.features.map((feat) => (
                  <div key={feat.title} className="bg-background-50 rounded-lg p-5 text-center">
                    <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-xl bg-primary-100 text-primary-500">
                      <i className={`${feat.icon} text-xl`}></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-2">{feat.title}</h3>
                    <p className="text-xs text-foreground-600 leading-relaxed">{feat.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-10 md:py-14">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 text-center mb-8">
                数据一览
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                {aboutContent.stats.map((stat) => (
                  <div key={stat.label} className="text-center">
                    <div className="text-3xl md:text-4xl font-bold font-heading text-primary-500 mb-1">
                      {stat.value}
                    </div>
                    <p className="text-sm text-foreground-600">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-10 md:py-14 bg-background-900 text-foreground-100">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-50 text-center mb-8">
                联系我们
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-3xl mx-auto">
                <div className="flex flex-col items-center gap-2 text-center">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/20 text-primary-400">
                    <i className="ri-mail-line text-xl"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-50">电子邮件</h3>
                  <p className="text-xs text-foreground-400">{siteInfo.email}</p>
                </div>
                <div className="flex flex-col items-center gap-2 text-center">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/20 text-primary-400">
                    <i className="ri-phone-line text-xl"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-50">客服电话</h3>
                  <p className="text-xs text-foreground-400">{siteInfo.phone}</p>
                </div>
                <div className="flex flex-col items-center gap-2 text-center">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/20 text-primary-400">
                    <i className="ri-map-pin-line text-xl"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-50">公司地址</h3>
                  <p className="text-xs text-foreground-400">{siteInfo.address}</p>
                </div>
              </div>
            </div>
          </section>

          <section className="py-10 md:py-14">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 text-center mb-8">
                常见问题
              </h2>
              <div className="max-w-2xl mx-auto space-y-3">
                {aboutContent.faqs.map((faq, index) => (
                  <div key={index} className="bg-background-50 rounded-lg border border-background-200 overflow-hidden">
                    <button
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      className="w-full flex items-center justify-between p-4 text-left cursor-pointer"
                    >
                      <span className="text-sm font-medium text-foreground-900 pr-4">{faq.question}</span>
                      <i
                        className={`ri-arrow-down-s-line text-foreground-500 transition-transform duration-300 flex-shrink-0 ${
                          openFaq === index ? 'rotate-180' : ''
                        }`}
                      ></i>
                    </button>
                    {openFaq === index && (
                      <div className="px-4 pb-4">
                        <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}